import React, { useState, useEffect } from 'react';

interface TimePeriod {
  start: string;
  end: string;
}

interface DayConfig {
  active: boolean;
  periods: TimePeriod[];
}

export interface DeliverySchedule {
  weekly: Record<string, DayConfig>;
}

interface TimetableDaySchedule {
  periods: TimePeriod[];
  non_business: boolean;
  is_24_7?: boolean;
}

interface TimetableData {
  weekly: Record<string, TimetableDaySchedule>;
  special_days: any[];
}

interface DeliveryScheduleDialogProps {
  open: boolean;
  title: string;
  schedule: DeliverySchedule | null;
  workingTimetable: TimetableData | null;
  onSave: (schedule: DeliverySchedule) => void;
  onCancel: () => void;
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
  monday: 'Mo', tuesday: 'Tu', wednesday: 'We', thursday: 'Th',
  friday: 'Fr', saturday: 'Sa', sunday: 'Su',
};

function getDefaultSchedule(timetable: TimetableData | null): DeliverySchedule {
  const weekly: Record<string, DayConfig> = {};
  for (const day of DAY_KEYS) {
    const tt = timetable?.weekly?.[day];
    if (tt && !tt.non_business) {
      weekly[day] = {
        active: true,
        periods: tt.periods?.length > 0 ? [...tt.periods] : [{ start: '07:00', end: '21:30' }],
      };
    } else {
      weekly[day] = { active: false, periods: [{ start: '07:00', end: '21:30' }] };
    }
  }
  return { weekly };
}

export const DeliveryScheduleDialog: React.FC<DeliveryScheduleDialogProps> = ({
  open,
  title,
  schedule,
  workingTimetable,
  onSave,
  onCancel,
}) => {
  const [localSchedule, setLocalSchedule] = useState<DeliverySchedule>(
    schedule || getDefaultSchedule(workingTimetable)
  );
  const [useWorkingHours, setUseWorkingHours] = useState(!schedule);
  const [error, setError] = useState('');

  useEffect(() => {
    setLocalSchedule(schedule || getDefaultSchedule(workingTimetable));
    setUseWorkingHours(!schedule);
  }, [schedule, workingTimetable]);

  if (!open) return null;

  const handleToggleDay = (day: string) => {
    setLocalSchedule(prev => ({
      ...prev,
      weekly: {
        ...prev.weekly,
        [day]: { ...prev.weekly[day], active: !prev.weekly[day]?.active },
      },
    }));
  };

  const handlePeriodChange = (day: string, idx: number, field: 'start' | 'end', value: string) => {
    setLocalSchedule(prev => {
      const dayConfig = { ...prev.weekly[day] };
      const periods = [...(dayConfig.periods || [])];
      periods[idx] = { ...periods[idx], [field]: value };
      return {
        ...prev,
        weekly: { ...prev.weekly, [day]: { ...dayConfig, periods } },
      };
    });
  };

  const addPeriod = (day: string) => {
    setLocalSchedule(prev => {
      const dayConfig = { ...prev.weekly[day] };
      const periods = [...(dayConfig.periods || [])];
      periods.push({ start: '12:00', end: '18:00' });
      return {
        ...prev,
        weekly: { ...prev.weekly, [day]: { ...dayConfig, periods } },
      };
    });
  };

  const removePeriod = (day: string, idx: number) => {
    setLocalSchedule(prev => {
      const dayConfig = { ...prev.weekly[day] };
      const periods = (dayConfig.periods || []).filter((_, i) => i !== idx);
      return {
        ...prev,
        weekly: { ...prev.weekly, [day]: { ...dayConfig, periods: periods.length > 0 ? periods : [{ start: '07:00', end: '21:30' }] } },
      };
    });
  };

  const handleUseWorkingHours = (checked: boolean) => {
    setUseWorkingHours(checked);
    if (checked) {
      setLocalSchedule(getDefaultSchedule(workingTimetable));
    }
  };

  const handleSave = () => {
    setError('');
    // Validate periods
    for (const day of DAY_KEYS) {
      const dayConfig = localSchedule.weekly[day];
      if (!dayConfig?.active) continue;
      for (const period of dayConfig.periods || []) {
        if (period.start >= period.end) {
          setError(`${day}: Start time must be before end time`);
          return;
        }
      }
    }

    if (useWorkingHours) {
      // null means "use working hours" on the backend
      onSave(null as any);
    } else {
      onSave(localSchedule);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{error}</div>
          )}

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useWorkingHours}
              onChange={(e) => handleUseWorkingHours(e.target.checked)}
              className="h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Use full working hours</span>
          </label>

          {!useWorkingHours && (
            <div className="space-y-3">
              {DAY_KEYS.map(day => {
                const dayConfig = localSchedule.weekly[day] || { active: false, periods: [] };
                return (
                  <div key={day} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dayConfig.active}
                          onChange={() => handleToggleDay(day)}
                          className="h-4 w-4 text-alps-primary focus:ring-alps-primary border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium">{DAY_LABELS[day]}</span>
                      </label>
                      {dayConfig.active && (dayConfig.periods?.length || 0) < 3 && (
                        <button
                          type="button"
                          onClick={() => addPeriod(day)}
                          className="text-xs text-alps-primary hover:text-alps-primary/80"
                        >
                          + Add period
                        </button>
                      )}
                    </div>
                    {dayConfig.active && (
                      <div className="space-y-2 ml-6">
                        {(dayConfig.periods || []).map((period, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <input
                              type="time"
                              value={period.start}
                              onChange={(e) => handlePeriodChange(day, idx, 'start', e.target.value)}
                              className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-alps-primary focus:border-alps-primary"
                            />
                            <span className="text-gray-400">→</span>
                            <input
                              type="time"
                              value={period.end}
                              onChange={(e) => handlePeriodChange(day, idx, 'end', e.target.value)}
                              className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-alps-primary focus:border-alps-primary"
                            />
                            {(dayConfig.periods?.length || 0) > 1 && (
                              <button
                                type="button"
                                onClick={() => removePeriod(day, idx)}
                                className="text-red-400 hover:text-red-600 text-sm"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-alps-primary border border-transparent rounded-md hover:bg-alps-primary/90"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};
